import {
    EVENT_TYPE,
    NOTIFICATION_TYPES,
    RESOURCE_TYPE,
    UPDATE_FREQUENCY,
} from '/b2b/notifications/constants';

const UPDATE_FREQUENCY_LABELS = {
    [UPDATE_FREQUENCY.NEVER]: 'notifications.updateFrequency.never',
    [UPDATE_FREQUENCY.DAILY]: 'notifications.updateFrequency.daily',
    [UPDATE_FREQUENCY.WEEKLY]: 'notifications.updateFrequency.weekly',
    [UPDATE_FREQUENCY.MONTHLY]: 'notifications.updateFrequency.monthly',
};

const UPDATE_FREQUENCY_DAILY_NEVER_LABELS = {
    [UPDATE_FREQUENCY.NEVER]: 'notifications.updateFrequency.never',
    [UPDATE_FREQUENCY.DAILY]: 'notifications.updateFrequency.daily',
    [UPDATE_FREQUENCY.WEEKLY]: 'notifications.updateFrequency.weekly',
};

const UPDATE_FREQUENCY_ALWAYS_NEVER_LABELS = {
    [UPDATE_FREQUENCY.NEVER]: 'notifications.updateFrequency.never',
    [UPDATE_FREQUENCY.ALWAYS]: 'notifications.updateFrequency.always',
};

const FREQUENCY_OPTIONS = {
    ...Object.entries(UPDATE_FREQUENCY_LABELS).reduce((options, [value, label]) => {
        options[value.toLowerCase()] = { label, value };

        return options;
    }, {}),
};

const DAILY_OR_NEVER_FREQUENCY_OPTIONS = {
    ...Object.entries(UPDATE_FREQUENCY_DAILY_NEVER_LABELS).reduce((options, [value, label]) => {
        options[value.toLowerCase()] = { label, value };

        return options;
    }, {}),
};

const ALWAYS_OR_NEVER_FREQUENCY_OPTIONS = {
    ...Object.entries(UPDATE_FREQUENCY_ALWAYS_NEVER_LABELS).reduce((options, [value, label]) => {
        options[value.toLowerCase()] = { label, value };

        return options;
    }, {}),
};

export function getPreferenceKeyPath(name) {
    const [, resourceType, eventType, preference] = name.split('.');
    return [resourceType, eventType, preference];
}

/**
 * Resolved field names (schema keys), kept in one place so the grouped card
 * layout (`CARD_GROUPS`) and `SCHEMA_OPTIONS` reference the exact same strings.
 */
export const FIELD_NAMES = {
    consentManager: `${RESOURCE_TYPE.CONSENTMANAGER}.${EVENT_TYPE.TATTLE}`,
    dataStoreScans: `${RESOURCE_TYPE.DISCO}.${EVENT_TYPE.SCAN}`,
    dsarUpdates: `${RESOURCE_TYPE.DSAR}.${EVENT_TYPE.DSAR_NEW_REQUEST_SUMMRY}`,
    dsarActionItem: `preferences.${RESOURCE_TYPE.DSAR}.${EVENT_TYPE.DSAR_NEW_REQUEST_SUMMRY}.actionItemNotify`,
    dsarPortalMessage: `preferences.${RESOURCE_TYPE.DSAR}.${EVENT_TYPE.DSAR_NEW_REQUEST_SUMMRY}.portalMessageNotify`,
    dsarReadyForReview: `preferences.${RESOURCE_TYPE.DSAR}.${EVENT_TYPE.DSAR_NEW_REQUEST_SUMMRY}.dsarReadyForReviewNotify`,
    legislation: `${RESOURCE_TYPE.LEGISLATION}.${EVENT_TYPE.LEGISLATION_POST_UPDATE}`,
    vendorUpdates: `${RESOURCE_TYPE.VENDOR}.${EVENT_TYPE.SCORE_UPDATE}`,
    vendorAssigned: `preferences.${RESOURCE_TYPE.VENDOR}.${EVENT_TYPE.SCORE_UPDATE}.assigned`,
    scoreThresholdEnabled: `preferences.${RESOURCE_TYPE.VENDOR}.${EVENT_TYPE.SCORE_UPDATE}.scoreThresholdEnabled`,
    scoreThresholdValue: `preferences.${RESOURCE_TYPE.VENDOR}.${EVENT_TYPE.SCORE_UPDATE}.scoreThresholdValue`,
    assessment: `${RESOURCE_TYPE.ASSESSMENT_ASSIGNMENT}.${EVENT_TYPE.ASSESSMENT_ASSIGNED}`,
    siteBreach: `${RESOURCE_TYPE.SITE_BREACH}.${EVENT_TYPE.VENDOR_BREACH}`,
};

/**
 * Group metadata that drives the grouped card layout. Each card
 * has a bold title + muted description and an ordered list of field `rows`
 * (schema keys); only fields the user has permission for are rendered, and empty
 * cards are skipped. `subgroups` render as inset panels (e.g. "Immediate alerts").
 */
export const CARD_GROUPS = [
    {
        id: 'cookieConsent',
        title: 'notifications.groups.cookieConsent.title',
        description: 'notifications.groups.cookieConsent.description',
        rows: [FIELD_NAMES.consentManager],
    },
    {
        id: 'subjectRights',
        title: 'notifications.groups.subjectRights.title',
        description: 'notifications.groups.subjectRights.description',
        rows: [FIELD_NAMES.dsarUpdates, FIELD_NAMES.dataStoreScans],
        subgroups: [
            {
                id: 'immediateAlerts',
                title: 'notifications.groups.immediateAlerts.title',
                description: 'notifications.groups.immediateAlerts.description',
                rows: [
                    FIELD_NAMES.dsarActionItem,
                    FIELD_NAMES.dsarPortalMessage,
                    FIELD_NAMES.dsarReadyForReview,
                ],
            },
        ],
    },
    {
        id: 'vendorRisk',
        title: 'notifications.groups.vendorRisk.title',
        description: 'notifications.groups.vendorRisk.description',
        rows: [
            FIELD_NAMES.vendorUpdates,
            FIELD_NAMES.vendorAssigned,
            FIELD_NAMES.scoreThresholdEnabled,
            FIELD_NAMES.scoreThresholdValue,
            FIELD_NAMES.siteBreach,
        ],
    },
    {
        id: 'complianceLegal',
        title: 'notifications.groups.complianceLegal.title',
        description: 'notifications.groups.complianceLegal.description',
        rows: [FIELD_NAMES.legislation, FIELD_NAMES.assessment],
    },
];

export const SCHEMA_OPTIONS = {
    /*
    [NOTIFICATION_TYPES.ASKANALYST]: {
        name: `${RESOURCE_TYPE.ASKANALYST}.${EVENT_TYPE.ASKANALYST_RESPONSE}`,
        label: 'Privacy Team',
        type: 'button',
        helperText: 'When you receive a response from the Privacy Team',
        formGroup: {
            options: FREQUENCY_OPTIONS,
        },
    },
    */
    [NOTIFICATION_TYPES.CONSENT_MANAGER]: {
        name: FIELD_NAMES.consentManager,
        label: 'notifications.consentManager.label',
        type: 'button',
        helperText: 'notifications.consentManager.theFirstTimeYouReceiveATattle',
        formGroup: {
            options: FREQUENCY_OPTIONS,
        },
    },
    [NOTIFICATION_TYPES.DATA_MAPPING]: {
        name: FIELD_NAMES.dataStoreScans,
        label: 'notifications.dataProvider.dataProviderScans',
        type: 'button',
        helperText: 'notifications.dataProvider.whenIsInitiated',
        formGroup: {
            options: FREQUENCY_OPTIONS,
        },
    },
    [NOTIFICATION_TYPES.DSAR]: {
        name: FIELD_NAMES.dsarUpdates,
        label: 'notifications.dsar.updates',
        type: 'button',
        helperText: 'notifications.dsar.requests',
        formGroup: {
            options: DAILY_OR_NEVER_FREQUENCY_OPTIONS,
        },
    },
    [`${NOTIFICATION_TYPES.DSAR}.preferences.actionItemNotify`]: {
        name: FIELD_NAMES.dsarActionItem,
        label: 'notifications.dsar.preferences.actionItemNotify',
        helperText: 'notifications.dsar.preferences.actionItemNotify.description',
        type: 'switch',
    },
    [`${NOTIFICATION_TYPES.DSAR}.preferences.portalMessageNotify`]: {
        name: FIELD_NAMES.dsarPortalMessage,
        label: 'notifications.dsar.preferences.portalMessageNotify',
        helperText: 'notifications.dsar.preferences.portalMessageNotify.description',
        type: 'switch',
    },
    [`${NOTIFICATION_TYPES.DSAR}.preferences.dsarReadyForReviewNotify`]: {
        name: FIELD_NAMES.dsarReadyForReview,
        label: 'notifications.dsar.preferences.dsarReadyForReviewNotify',
        helperText: 'notifications.dsar.preferences.dsarReadyForReviewNotify.description',
        type: 'switch',
    },
    [NOTIFICATION_TYPES.LEGISLATION]: {
        name: FIELD_NAMES.legislation,
        label: 'notifications.legislation.privacyLawUpdates',
        type: 'button',
        helperText: 'notifications.legislation.legislationIsPending',
        formGroup: {
            options: FREQUENCY_OPTIONS,
        },
    },
    [NOTIFICATION_TYPES.VENDOR]: {
        name: FIELD_NAMES.vendorUpdates,
        label: 'notifications.vendor.updates',
        type: 'button',
        helperText: 'notifications.vendor.privacyScoreChanges',
        formGroup: {
            options: FREQUENCY_OPTIONS,
        },
    },
    [`${NOTIFICATION_TYPES.VENDOR}.preferences.assigned`]: {
        name: FIELD_NAMES.vendorAssigned,
        label: 'notifications.vendor.preferences.updates',
        type: 'switch',
    },
    [`${NOTIFICATION_TYPES.VENDOR}.preferences.scoreThresholdEnabled`]: {
        name: FIELD_NAMES.scoreThresholdEnabled,
        label: 'notifications.vendor.privacyScore.enabled.label',
        type: 'switch',
    },
    [`${NOTIFICATION_TYPES.VENDOR}.preferences.scoreThreshold`]: {
        depends: {
            [FIELD_NAMES.scoreThresholdEnabled]: true,
        },
        numericality: { strict: true },
        name: FIELD_NAMES.scoreThresholdValue,
        label: 'notifications.vendor.privacyScore.threshold.label',
        type: 'slider',
        props: {
            inputSlider: false,
            min: 0,
            max: 100,
            step: 1,
            marks: [0, 25, 50, 75, 100].map(value => ({ value, label: value })),
            style: { maxWidth: '50%' },
        },
    },
    [NOTIFICATION_TYPES.ASSESSMENT]: {
        name: FIELD_NAMES.assessment,
        label: 'notifications.assessments',
        type: 'button',
        helperText: 'notifications.assessment.assessmentAssignment',
        formGroup: {
            options: ALWAYS_OR_NEVER_FREQUENCY_OPTIONS,
        },
    },
    [NOTIFICATION_TYPES.SITE_BREACH]: {
        name: FIELD_NAMES.siteBreach,
        label: 'notifications.vendorBreaches',
        type: 'button',
        helperText: 'notifications.vendor.vendorBreaches',
        formGroup: {
            options: ALWAYS_OR_NEVER_FREQUENCY_OPTIONS,
        },
    },
};

export const notificationDefaults = {
    [FIELD_NAMES.dsarActionItem]: false,
    [FIELD_NAMES.dsarPortalMessage]: false,
    [FIELD_NAMES.dsarReadyForReview]: false,
    [FIELD_NAMES.vendorAssigned]: false,
    [FIELD_NAMES.scoreThresholdEnabled]: false,
    [FIELD_NAMES.scoreThresholdValue]: 75,
};

export const createHelperText = notificationPermissions =>
    notificationPermissions.reduce((helperTextOptions, value) => {
        if (SCHEMA_OPTIONS[value]) {
            const { helperText, name } = SCHEMA_OPTIONS[value];
            helperTextOptions[name] = helperText || '';
        }

        return helperTextOptions;
    }, {});

export default (notificationPermissions = []) =>
    notificationPermissions.reduce((schema, value) => {
        if (!schema[value] && SCHEMA_OPTIONS[value]) {
            const { helperText, name, ...remain } = SCHEMA_OPTIONS[value];
            schema[name] = remain;
        }
        for (const option of Object.keys(SCHEMA_OPTIONS)) {
            const { name, ...remain } = SCHEMA_OPTIONS[option];
            if (!schema[name] && option.startsWith(value)) {
                schema[name] = remain;
            }
        }
        return schema;
    }, {});
